import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { 
  Home, ChevronRight, Building2, Briefcase, Award, Users, 
  MapPin, UserPlus, MessageSquare, Phone, Mail, Clock,
  CheckCircle, Star, Target, TrendingUp, Zap, Shield,
  Globe, Heart, Send, FileCheck, ExternalLink
} from "lucide-react";
import { useIsMobile } from "../components/ui/use-mobile";

// 子页签配置
const tabs = [
  { id: "feedback", name: "信息反馈", icon: MessageSquare },
  { id: "contact", name: "联系我们", icon: Phone },
];

export default function AboutPage() {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || "jobs");
  
  // 当 URL 参数变化时，更新 activeTab
  useEffect(() => {
    if (tabFromUrl && ['feedback', 'contact'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // 切换 tab 时同步 URL
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const [feedbackForm, setFeedbackForm] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "",
    message: ""
  });

  const renderContent = () => {
    switch (activeTab) {
      case "feedback":
        return <FeedbackForm form={feedbackForm} setForm={setFeedbackForm} isMobile={isMobile} />;
      case "contact":
        return <ContactUs isMobile={isMobile} />;
      default:
        return <FeedbackForm form={feedbackForm} setForm={setFeedbackForm} isMobile={isMobile} />;
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Header />

      {/* 页面横幅 */}
      <div style={{
        background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 50%, #3182ce 100%)',
        padding: isMobile ? '32px 0' : '60px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-100px',
          right: '-100px',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-150px',
          left: '10%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)'
        }} />
        
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px', marginBottom: isMobile ? '12px' : '16px' }}>
            <Building2 style={{ width: isMobile ? '28px' : '40px', height: isMobile ? '28px' : '40px', color: '#fbbf24' }} />
            <h1 style={{ 
              fontSize: isMobile ? '24px' : '36px', 
              fontWeight: 700, 
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              关于我们
            </h1>
          </div>
          <p style={{ fontSize: isMobile ? '14px' : '16px', color: 'rgba(255,255,255,0.8)' }}>
            德正润业·慧达通规
          </p>
        </div>
      </div>

      {/* 面包屑导航 */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '12px 0' : '16px 0' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px', fontSize: isMobile ? '12px' : '14px', flexWrap: 'wrap' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none' }}>
              <Home style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
              首页
            </Link>
            <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px', color: '#d1d5db' }} />
            <span style={{ color: '#2c5282', fontWeight: 500 }}>关于我们</span>
            <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px', color: '#d1d5db' }} />
            <span style={{ color: '#1a365d', fontWeight: 500 }}>
              {tabs.find(t => t.id === activeTab)?.name}
            </span>
          </nav>
        </div>
      </div>

      {/* 主内容区域 */}
      <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '20px 0' : '40px 0' }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '20px' : '40px' }}>
          {/* 左侧导航 */}
          {!isMobile && (
            <div style={{ width: '240px', flexShrink: 0 }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                position: 'sticky',
                top: '32px'
              }}>
                <div style={{
                  padding: '20px 24px',
                  background: 'linear-gradient(135deg, #1a365d, #2c5282)',
                  color: 'white'
                }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 600 }}>导航菜单</h3>
                </div>
                <div style={{ padding: '12px' }}>
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    const isHovered = hoveredTab === tab.id;
                    
                    return (
                      <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id)}
                        onMouseEnter={() => setHoveredTab(tab.id)}
                        onMouseLeave={() => setHoveredTab(null)}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '14px 16px',
                          border: 'none',
                          borderRadius: '10px',
                          backgroundColor: isActive 
                            ? '#1a365d' 
                            : isHovered 
                              ? '#f0f9ff' 
                              : 'transparent',
                          color: isActive ? 'white' : '#374151',
                          fontSize: '15px',
                          fontWeight: isActive ? 600 : 400,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          marginBottom: '4px',
                          textAlign: 'left'
                        }}
                      >
                        <Icon style={{ 
                          width: '18px', 
                          height: '18px',
                          color: isActive ? 'white' : '#6b7280'
                        }} />
                        {tab.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* 移动端顶部标签导航 */}
          {isMobile && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              padding: '12px',
              display: 'flex',
              gap: '8px',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}>
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: isActive ? '#1a365d' : '#f3f4f6',
                      color: isActive ? 'white' : '#374151',
                      fontSize: '13px',
                      fontWeight: isActive ? 600 : 400,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}
                  >
                    <Icon style={{ 
                      width: '14px', 
                      height: '14px',
                      color: isActive ? 'white' : '#6b7280'
                    }} />
                    {tab.name}
                  </button>
                );
              })}
            </div>
          )}

          {/* 右侧内容 */}
          <div style={{ flex: 1 }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: isMobile ? '12px' : '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              padding: isMobile ? '20px' : '40px',
              minHeight: isMobile ? 'auto' : '600px'
            }}>
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

// 公司介绍组件
function CompanyIntro() {
  return (
    <div>
      <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#1a365d', marginBottom: '32px', paddingBottom: '16px', borderBottom: '3px solid #1a365d' }}>
        公司介绍
      </h2>
      
      <div style={{ marginBottom: '40px' }}>
        <p style={{ fontSize: '16px', lineHeight: 2, color: '#4b5563', marginBottom: '20px', textAlign: 'justify' }}>
          <strong style={{ color: '#1a365d' }}>德润慧通工程项目管理(四川)有限公司</strong>成立于2008年，是经四川省财政厅批准设立的专业招标代理机构，
          具有政府采购代理甲级资质、工程招标代理甲级资质。公司注册资本1000万元，总部位于成都市高新区天府大道。
        </p>
        <p style={{ fontSize: '16px', lineHeight: 2, color: '#4b5563', marginBottom: '20px', textAlign: 'justify' }}>
          经过十余年的发展，公司已成为四川省内规模较大、实力雄厚的综合性招标代理服务机构。我们始终秉承
          <strong style={{ color: '#e53e3e' }}>"专业规范、用心服务"</strong>的经营理念，为各级政府机关、事业单位、
          国有企业及社会组织提供全方位的招标采购咨询服务。
        </p>
      </div>

      {/* 核心数据 */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '20px',
        marginBottom: '40px'
      }}>
        {[
          { number: '16+', label: '年行业经验', color: '#1a365d' },
          { number: '5000+', label: '累计服务项目', color: '#2c5282' },
          { number: '100+', label: '专业团队成员', color: '#3182ce' },
          { number: '500亿+', label: '累计采购金额', color: '#4299e1' }
        ].map((item, index) => (
          <div key={index} style={{
            textAlign: 'center',
            padding: '28px 20px',
            background: `linear-gradient(135deg, ${item.color}10, ${item.color}05)`,
            borderRadius: '12px',
            border: `1px solid ${item.color}20`
          }}>
            <div style={{ fontSize: '36px', fontWeight: 700, color: item.color, marginBottom: '8px' }}>
              {item.number}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>{item.label}</div>
          </div>
        ))}
      </div>

      {/* 企业文化 */}
      <div>
        <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#1a365d', marginBottom: '24px' }}>企业文化</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
          {[
            { icon: Target, title: '企业使命', desc: '为客户创造价值，为行业树立标杆' },
            { icon: Star, title: '企业愿景', desc: '成为西部地区最具影响力的招标代理服务商' },
            { icon: Heart, title: '核心价值观', desc: '诚信、专业、创新、共赢' },
            { icon: Shield, title: '服务承诺', desc: '规范操作、公平公正、高效服务、保密安全' }
          ].map((item, index) => (
            <div key={index} style={{
              display: 'flex',
              gap: '16px',
              padding: '24px',
              backgroundColor: '#f8fafc',
              borderRadius: '12px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                backgroundColor: '#1a365d',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <item.icon style={{ width: '24px', height: '24px', color: 'white' }} />
              </div>
              <div>
                <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>{item.title}</h4>
                <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: 1.6 }}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// 业务范围组件
function BusinessScope() {
  const services = [
    {
      icon: Briefcase,
      title: "政府采购代理",
      desc: "为各级政府机关、事业单位提供货物、服务、工程的政府采购代理服务",
      items: ["公开招标", "竞争性谈判", "询价采购", "单一来源采购", "竞争性磋商"]
    },
    {
      icon: Building2,
      title: "工程招标代理",
      desc: "提供各类建设工程项目的招标代理全过程服务",
      items: ["施工招标", "监理招标", "设计招标", "EPC总承包招标", "勘察招标"]
    },
    {
      icon: Globe,
      title: "国有产权交易",
      desc: "为国有企业提供产权转让、增资扩股等交易服务",
      items: ["产权转让", "增资扩股", "资产处置", "企业改制", "租赁交易"]
    },
    {
      icon: Zap,
      title: "咨询服务",
      desc: "提供招标采购全流程的专业咨询顾问服务",
      items: ["采购方案策划", "招标文件编制", "评审专家管理", "合同管理咨询", "争议解决支持"]
    }
  ];

  return (
    <div>
      <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#1a365d', marginBottom: '32px', paddingBottom: '16px', borderBottom: '3px solid #1a365d' }}>
        业务范围
      </h2>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {services.map((service, index) => (
          <div key={index} style={{
            padding: '32px',
            border: '1px solid #e5e7eb',
            borderRadius: '16px',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #1a365d, #2c5282)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <service.icon style={{ width: '32px', height: '32px', color: 'white' }} />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#1f2937', marginBottom: '12px' }}>
                  {service.title}
                </h3>
                <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '20px', lineHeight: 1.7 }}>
                  {service.desc}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {service.items.map((item, idx) => (
                    <span key={idx} style={{
                      padding: '6px 14px',
                      backgroundColor: '#eff6ff',
                      color: '#1e40af',
                      fontSize: '13px',
                      borderRadius: '20px'
                    }}>
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// 项目业绩组件
function ProjectAchievements() {
  const projects = [
    {
      year: "2024",
      items: [
        { name: "成都市第一人民医院医疗设备采购项目", amount: "5,000万元", type: "货物类" },
        { name: "四川省政府办公楼信息化建设项目", amount: "8,500万元", type: "服务类" },
        { name: "绵阳市交通基础设施建设工程", amount: "2.3亿元", type: "工程类" }
      ]
    },
    {
      year: "2023",
      items: [
        { name: "德阳市智慧城市建设项目", amount: "1.2亿元", type: "服务类" },
        { name: "自贡市公共交通车辆采购项目", amount: "6,800万元", type: "货物类" },
        { name: "宜宾市污水处理厂升级改造工程", amount: "1.8亿元", type: "工程类" }
      ]
    },
    {
      year: "2022",
      items: [
        { name: "泸州市教育信息化装备采购项目", amount: "4,500万元", type: "货物类" },
        { name: "南充市医疗卫生服务体系建设", amount: "9,200万元", type: "服务类" },
        { name: "达州市高速公路建设工程", amount: "15亿元", type: "工程类" }
      ]
    }
  ];

  return (
    <div>
      <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#1a365d', marginBottom: '32px', paddingBottom: '16px', borderBottom: '3px solid #1a365d' }}>
        项目业绩
      </h2>

      {/* 统计数据 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px',
        marginBottom: '40px'
      }}>
        {[
          { label: '2024年代理金额', value: '38.5亿元', icon: TrendingUp },
          { label: '2024年代理项目', value: '486个', icon: Briefcase },
          { label: '客户满意度', value: '98.6%', icon: Star }
        ].map((item, index) => (
          <div key={index} style={{
            padding: '24px',
            background: 'linear-gradient(135deg, #1a365d, #2c5282)',
            borderRadius: '12px',
            color: 'white',
            textAlign: 'center'
          }}>
            <item.icon style={{ width: '32px', height: '32px', marginBottom: '12px', opacity: 0.9 }} />
            <div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px' }}>{item.value}</div>
            <div style={{ fontSize: '14px', opacity: 0.8 }}>{item.label}</div>
          </div>
        ))}
      </div>

      {/* 项目列表 */}
      {projects.map((yearData, yearIndex) => (
        <div key={yearIndex} style={{ marginBottom: '32px' }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 600,
            color: '#1a365d',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{
              padding: '4px 16px',
              backgroundColor: '#1a365d',
              color: 'white',
              borderRadius: '20px',
              fontSize: '16px'
            }}>
              {yearData.year}
            </span>
            年度代表项目
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {yearData.items.map((item, itemIndex) => (
              <div key={itemIndex} style={{
                display: 'flex',
                alignItems: 'center',
                padding: '20px 24px',
                backgroundColor: '#f8fafc',
                borderRadius: '10px',
                gap: '16px'
              }}>
                <CheckCircle style={{ width: '20px', height: '20px', color: '#10b981', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: 500, color: '#1f2937' }}>{item.name}</div>
                </div>
                <span style={{
                  padding: '4px 12px',
                  backgroundColor: '#dbeafe',
                  color: '#1e40af',
                  fontSize: '12px',
                  borderRadius: '20px'
                }}>
                  {item.type}
                </span>
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#e53e3e' }}>{item.amount}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// 人员优势组件
function TeamAdvantages() {
  const teamStats = [
    { label: '总员工人数', value: '128人' },
    { label: '高级职称', value: '45人' },
    { label: '注册造价师', value: '18人' },
    { label: '硕士及以上学历', value: '32人' }
  ];

  const departments = [
    {
      name: '项目运营部',
      count: 42,
      desc: '负责项目全流程管理，确保项目高效运转'
    },
    {
      name: '采购咨询部',
      count: 28,
      desc: '提供专业采购方案策划和文件编制服务'
    },
    {
      name: '法务合规部',
      count: 15,
      desc: '保障项目合规性，处理争议和投诉事务'
    },
    {
      name: '技术支持部',
      count: 18,
      desc: '负责电子采购平台运维和技术创新'
    },
    {
      name: '客户服务部',
      count: 12,
      desc: '提供7x24小时客户咨询和售后服务'
    },
    {
      name: '综合管理部',
      count: 13,
      desc: '负责人事、财务、行政等综合管理工作'
    }
  ];

  return (
    <div>
      <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#1a365d', marginBottom: '32px', paddingBottom: '16px', borderBottom: '3px solid #1a365d' }}>
        人员优势
      </h2>

      {/* 团队统计 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '20px',
        marginBottom: '40px'
      }}>
        {teamStats.map((stat, index) => (
          <div key={index} style={{
            textAlign: 'center',
            padding: '24px',
            backgroundColor: '#f8fafc',
            borderRadius: '12px'
          }}>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#1a365d', marginBottom: '8px' }}>
              {stat.value}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* 部门介绍 */}
      <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#1a365d', marginBottom: '24px' }}>部门设置</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: '40px' }}>
        {departments.map((dept, index) => (
          <div key={index} style={{
            padding: '24px',
            border: '1px solid #e5e7eb',
            borderRadius: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>{dept.name}</h4>
              <span style={{
                padding: '4px 12px',
                backgroundColor: '#1a365d',
                color: 'white',
                fontSize: '13px',
                borderRadius: '20px'
              }}>
                {dept.count}人
              </span>
            </div>
            <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: 1.6 }}>{dept.desc}</p>
          </div>
        ))}
      </div>

      {/* 专业资质 */}
      <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#1a365d', marginBottom: '24px' }}>专业资质</h3>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px'
      }}>
        {[
          '政府采购代理甲级资质',
          '工程招标代理甲级资质',
          '国有产权交易机构会员',
          '四川省招标协会理事单位',
          'ISO9001质量管理认证',
          'ISO27001信息安全认证'
        ].map((cert, index) => (
          <div key={index} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px 20px',
            backgroundColor: '#eff6ff',
            borderRadius: '8px'
          }}>
            <Award style={{ width: '20px', height: '20px', color: '#1a365d', flexShrink: 0 }} />
            <span style={{ fontSize: '14px', color: '#1f2937' }}>{cert}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// 办公环境组件
function OfficeEnvironment() {
  const facilities = [
    { name: '总办公面积', value: '3,000㎡', desc: '位于成都市高新区核心商务区' },
    { name: '标准开标室', value: '8间', desc: '配备高清监控、音视频系统' },
    { name: '评标室', value: '12间', desc: '独立封闭，符合保密要求' },
    { name: '会议室', value: '6间', desc: '可容纳20-100人不等' },
    { name: '培训教室', value: '2间', desc: '配备投影、音响设备' },
    { name: '客户洽谈室', value: '10间', desc: '温馨舒适的商务环境' }
  ];

  return (
    <div>
      <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#1a365d', marginBottom: '32px', paddingBottom: '16px', borderBottom: '3px solid #1a365d' }}>
        办公环境
      </h2>

      <p style={{ fontSize: '16px', lineHeight: 2, color: '#4b5563', marginBottom: '32px' }}>
        公司总部位于成都市高新区天府大道，交通便利，周边配套完善。办公区域装修现代、设施齐全，
        为员工和客户提供舒适、高效的工作与服务环境。
      </p>

      {/* 设施统计 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px',
        marginBottom: '40px'
      }}>
        {facilities.map((item, index) => (
          <div key={index} style={{
            padding: '24px',
            backgroundColor: '#f8fafc',
            borderRadius: '12px',
            borderLeft: '4px solid #1a365d'
          }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#1a365d', marginBottom: '8px' }}>
              {item.value}
            </div>
            <div style={{ fontSize: '16px', fontWeight: 500, color: '#1f2937', marginBottom: '8px' }}>
              {item.name}
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>{item.desc}</div>
          </div>
        ))}
      </div>

      {/* 环境图片占位 */}
      <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#1a365d', marginBottom: '24px' }}>环境展示</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
        {['前台接待区', '开标大厅', '评标室', '会议室', '办公区域', '休息区'].map((area, index) => (
          <div key={index} style={{
            height: '180px',
            background: 'linear-gradient(135deg, #e5e7eb, #f3f4f6)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9ca3af',
            fontSize: '14px'
          }}>
            {area}（图片占位）
          </div>
        ))}
      </div>
    </div>
  );
}

// 招贤纳士组件
function JobRecruitment({ isMobile }: { isMobile: boolean }) {
  const jobs = [
    {
      title: '事业合伙人',
      department: '总经办',
      location: '四川省范围内',
      salary: '年薪 + 项目分红（具体面议）',
      requirements: [
        '拥有 5 年以上招标行业资源 / 市场拓展经验，熟悉政府采购、工程招标领域',
        '具备独立带团队开拓业务的能力，有成功项目操盘案例',
        '认同公司发展理念，愿与团队长期绑定、共享收益'
      ]
    },
    {
      title: '政府采购招标项目经理',
      department: '政府采购项目部',
      location: '成都',
      salary: '6-15K / 月',
      requirements: [
        '本科及以上学历，4 年以上政府采购招标项目管理经验',
        '精通政府采购相关法规，持有招标师等资质优先',
        '能独立把控招标全流程，具备良好的客户沟通与项目统筹能力'
      ]
    },
    {
      title: '工程招标项目经理',
      department: '工程项目部',
      location: '成都',
      salary: '6-15K / 月',
      requirements: [
        '本科及以上学历，5 年以上工程招标项目管理经验',
        '熟悉工程行业规范与造价知识，持有工程招标相关经验',
        '能独立对接甲方需求，协调多方资源完成项目落地'
      ]
    },
    {
      title: '项目助理',
      department: '政府采购项目部',
      location: '成都',
      salary: '5-10K / 月',
      requirements: [
        '大专及以上学历，1 年以上招标 / 项目助理相关经验',
        '熟练使用 Office 办公软件，了解招标流程基础规范',
        '细心严谨，能协助项目经理完成文档、流程跟进工作'
      ]
    }
  ];

  return (
    <div>
      <h2 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, color: '#1a365d', marginBottom: isMobile ? '20px' : '32px', paddingBottom: isMobile ? '12px' : '16px', borderBottom: '3px solid #1a365d' }}>
        招贤纳士 | 诚邀共创招标行业新蓝图
      </h2>

      <p style={{ fontSize: isMobile ? '14px' : '16px', lineHeight: 2, color: '#4b5563', marginBottom: isMobile ? '20px' : '32px' }}>
        我们诚邀事业合伙人及各岗位精英加入德润慧通大家庭，在这里你能收获有竞争力的薪酬福利、清晰的职业成长路径，与专业团队共拓招标领域新机遇！
      </p>

      {/* 福利保障 */}
      <div style={{ marginBottom: isMobile ? '24px' : '40px' }}>
        <h3 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 600, color: '#1a365d', marginBottom: isMobile ? '16px' : '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: isMobile ? '18px' : '20px' }}>✨</span>
          我们的福利保障
        </h3>
        <div style={{
          display: 'flex',
          gap: isMobile ? '8px' : '12px',
          flexWrap: 'wrap',
          padding: isMobile ? '16px' : '20px',
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}>
          {['五险一金', '年终奖金', '带薪年假', '节日福利', '定期体检', '培训晋升', '团建活动', '弹性工作'].map((benefit, index) => (
            <span key={index} style={{
              padding: isMobile ? '6px 14px' : '8px 18px',
              background: 'linear-gradient(135deg, #1a365d, #2c5282)',
              color: 'white',
              fontSize: isMobile ? '12px' : '14px',
              borderRadius: '20px',
              fontWeight: 500
            }}>
              {benefit}
            </span>
          ))}
        </div>
      </div>

      {/* 职位列表 */}
      <h3 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 600, color: '#1a365d', marginBottom: isMobile ? '20px' : '24px' }}>在招职位</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '20px' : '24px' }}>
        {jobs.map((job, index) => (
          <div key={index} style={{
            padding: isMobile ? '20px' : '28px',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            backgroundColor: '#ffffff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>
              <h4 style={{ 
                fontSize: isMobile ? '18px' : '20px', 
                fontWeight: 600, 
                color: '#1a365d', 
                marginBottom: isMobile ? '8px' : '12px' 
              }}>
                {job.title}
              </h4>
              <div style={{ 
                display: 'flex', 
                gap: isMobile ? '8px' : '12px', 
                fontSize: isMobile ? '13px' : '14px', 
                color: '#6b7280',
                flexWrap: 'wrap',
                marginBottom: isMobile ? '8px' : '10px'
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ fontWeight: 500 }}>所属部门：</span>{job.department}
                </span>
                <span>|</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ fontWeight: 500 }}>工作地点：</span>{job.location}
                </span>
              </div>
              <div style={{ 
                fontSize: isMobile ? '14px' : '15px', 
                color: '#e53e3e',
                fontWeight: 600
              }}>
                <span style={{ color: '#6b7280', fontWeight: 500 }}>薪酬结构：</span>{job.salary}
              </div>
            </div>
            
            <div style={{ marginTop: isMobile ? '16px' : '20px' }}>
              <div style={{ 
                fontSize: isMobile ? '14px' : '15px', 
                fontWeight: 600, 
                color: '#374151',
                marginBottom: isMobile ? '12px' : '16px'
              }}>
                任职要求：
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '8px' : '10px' }}>
                {job.requirements.map((req, reqIndex) => (
                  <div key={reqIndex} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    fontSize: isMobile ? '13px' : '14px',
                    color: '#4b5563',
                    lineHeight: 1.6
                  }}>
                    <span style={{ 
                      color: '#1a365d', 
                      fontWeight: 600,
                      marginTop: '2px',
                      flexShrink: 0
                    }}>
                      {reqIndex + 1}.
                    </span>
                    <span>{req}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ 
        marginTop: isMobile ? '32px' : '40px',
        padding: isMobile ? '20px' : '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        textAlign: 'center'
      }}>
        <div style={{ 
          fontSize: isMobile ? '16px' : '18px', 
          fontWeight: 600, 
          color: '#1a365d',
          marginBottom: isMobile ? '8px' : '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <Mail style={{ width: isMobile ? '18px' : '20px', height: isMobile ? '18px' : '20px' }} />
          <span>简历投递</span>
        </div>
        <div style={{ 
          fontSize: isMobile ? '14px' : '16px', 
          color: '#4b5563'
        }}>
          <span style={{ color: '#1a365d', fontWeight: 600 }}>scdrht@163.com</span>
        </div>
      </div>
    </div>
  );
}

// 信息反馈组件
function FeedbackForm({ form, setForm, isMobile }: { form: any; setForm: any; isMobile: boolean }) {
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const resp = await fetch('/api/public/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_name: form.name,
          phone: form.phone,
          email: form.email || null,
          feedback_topic: form.subject,
          feedback_content: form.message
        })
      });
      const data = await resp.json();
      if (data.ok) {
        alert('感谢您的反馈！我们会尽快处理。');
        setForm({ name: '', phone: '', email: '', subject: '', message: '' });
      } else {
        alert(data.error || '提交失败，请重试');
      }
    } catch (err) {
      console.error('反馈提交错误:', err);
      alert('提交失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h2 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, color: '#1a365d', marginBottom: isMobile ? '20px' : '32px', paddingBottom: isMobile ? '12px' : '16px', borderBottom: '3px solid #1a365d' }}>
        信息反馈
      </h2>

      <p style={{ fontSize: isMobile ? '14px' : '16px', lineHeight: 2, color: '#4b5563', marginBottom: isMobile ? '20px' : '32px' }}>
        我们重视每一位客户的意见和建议。如果您对我们的服务有任何疑问、建议或投诉，请填写以下表单，
        我们将在24小时内与您取得联系。
      </p>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '16px' : '20px', marginBottom: isMobile ? '16px' : '20px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
              您的姓名 <span style={{ color: '#e53e3e' }}>*</span>
            </label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '15px',
                outline: 'none'
              }}
              placeholder="请输入您的姓名"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
              联系电话 <span style={{ color: '#e53e3e' }}>*</span>
            </label>
            <input
              type="tel"
              required
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '15px',
                outline: 'none'
              }}
              placeholder="请输入您的联系电话"
            />
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
            电子邮箱
          </label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '15px',
              outline: 'none'
            }}
            placeholder="请输入您的电子邮箱"
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
            反馈主题 <span style={{ color: '#e53e3e' }}>*</span>
          </label>
          <select
            required
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '15px',
              outline: 'none',
              backgroundColor: 'white'
            }}
          >
            <option value="">请选择反馈主题</option>
            <option value="咨询">业务咨询</option>
            <option value="建议">意见建议</option>
            <option value="投诉">服务投诉</option>
            <option value="合作">商务合作</option>
            <option value="其他">其他</option>
          </select>
        </div>

        <div style={{ marginBottom: '32px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
            详细内容 <span style={{ color: '#e53e3e' }}>*</span>
          </label>
          <textarea
            required
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            rows={6}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '15px',
              outline: 'none',
              resize: 'vertical'
            }}
            placeholder="请详细描述您的问题或建议..."
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%',
            padding: '14px',
            backgroundColor: submitting ? '#9ca3af' : '#1a365d',
            color: 'white',
            fontSize: '16px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '8px',
            cursor: submitting ? 'not-allowed' : 'pointer'
          }}
        >
          <Send style={{ width: '18px', height: '18px' }} />
          {submitting ? '提交中...' : '提交反馈'}
        </button>
      </form>
    </div>
  );
}



// 联系我们组件
function ContactUs({ isMobile }: { isMobile: boolean }) {
  const contactInfo = [
    { icon: MapPin, label: '公司地址', value: '成都市高新区天府大道北段1700号6-1-510号' },
    { icon: Phone, label: '咨询电话', value: '028-62073616' },
    { icon: Mail, label: '电子邮箱', value: 'scdrht@163.com ' },
    { icon: Clock, label: '工作时间', value: '周一至周五 9:00-18:00' }
  ];


  return (
    <div>
      <h2 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, color: '#1a365d', marginBottom: isMobile ? '20px' : '32px', paddingBottom: isMobile ? '12px' : '16px', borderBottom: '3px solid #1a365d' }}>
        联系我们
      </h2>

      {/* 联系信息卡片 */}
      <div style={{
        padding: isMobile ? '20px' : '32px',
        background: 'linear-gradient(135deg, #1a365d, #2c5282)',
        borderRadius: isMobile ? '12px' : '16px',
        color: 'white',
        marginBottom: isMobile ? '24px' : '40px'
      }}>
        <h3 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 600, marginBottom: isMobile ? '20px' : '24px' }}>联系方式</h3>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? '16px' : '24px' }}>
          {contactInfo.map((item, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={{
                width: isMobile ? '36px' : '44px',
                height: isMobile ? '36px' : '44px',
                borderRadius: '10px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <item.icon style={{ width: isMobile ? '18px' : '22px', height: isMobile ? '18px' : '22px' }} />
              </div>
              <div>
                <div style={{ fontSize: isMobile ? '12px' : '13px', opacity: 0.8, marginBottom: '4px' }}>{item.label}</div>
                <div style={{ fontSize: isMobile ? '13px' : '15px', fontWeight: 500 }}>{item.value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      
    </div>
  );
}

